/*
 * Ported from the Next app. Two changes, both forced by there being no server:
 *
 *  * the sport comes off the router rather than an awaited `params` promise,
 *  * requests go to the admin service directly instead of through this app's
 *    `/api/scoring` route handler, which no longer exists.
 *
 * Everything below that line is the original screen.
 */
import { Link, useParams } from "react-router-dom";

import { apiFetch } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";

const SCORING_ROOT = `${getAdminApiBaseUrl()}/scoring`;

/**
 * The scoring-UI registry, as a screen.
 *
 * A scoring UI is an external page — hosted anywhere — that scores fixtures
 * through the console's iframe bridge. Registering one is a name, a URL, and
 * which kinds or configs it is built for; the fixture form offers the matches,
 * and the console resolves the pick. This screen is the registry's front door:
 * everything here talks to `/scoring/uis`, which is also what the seed writes.
 */

import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Globe, Pencil, Plus, Trash2 } from "lucide-react";

import styles from "./ScoringUisPage.module.css";

type UiRow = {
  id: string;
  sportCode: string;
  code: string;
  name: string;
  url: string;
  appliesTo: string[];
  description: string | null;
};

type ScopeOption = { code: string; label: string; group: "kind" | "config" };

type Draft = {
  code: string;
  name: string;
  url: string;
  appliesTo: string[];
  description: string;
};

const EMPTY: Draft = { code: "", name: "", url: "", appliesTo: [], description: "" };

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  return text ? (JSON.parse(text) as Record<string, unknown>) : {};
}

export function ScoringUisPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const [rows, setRows] = useState<UiRow[]>([]);
  const [options, setOptions] = useState<ScopeOption[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  /** null = closed; "new" = registering; an id = editing that row. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const response = await apiFetch(`${SCORING_ROOT}/uis?sport=${encodeURIComponent(sport)}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        setProblem(`the admin API answered ${response.status}`);
        return;
      }
      setRows((await response.json()) as UiRow[]);
      setProblem(null);
    } catch {
      setProblem("the admin API is not reachable");
    }
  }, [sport]);

  useEffect(() => {
    let alive = true;
    apiFetch(`${SCORING_ROOT}/uis?sport=${encodeURIComponent(sport)}`, { cache: "no-store" })
      .then(async (response) => {
        if (!alive) return;
        if (!response.ok) setProblem(`the admin API answered ${response.status}`);
        else {
          setRows((await response.json()) as UiRow[]);
          setProblem(null);
        }
      })
      .catch(() => alive && setProblem("the admin API is not reachable"));
    apiFetch(`${SCORING_ROOT}/uis/options?sport=${encodeURIComponent(sport)}`, { cache: "no-store" })
      .then(async (response) => {
        if (alive && response.ok) setOptions((await response.json()) as ScopeOption[]);
      })
      .catch(() => {
        // The picker degrades to raw codes; the page still works.
      });
    return () => {
      alive = false;
    };
  }, [sport]);

  const openNew = () => {
    setDraft(EMPTY);
    setFormError(null);
    setEditing("new");
  };

  const openEdit = (row: UiRow) => {
    setDraft({
      code: row.code,
      name: row.name,
      url: row.url,
      appliesTo: row.appliesTo,
      description: row.description ?? "",
    });
    setFormError(null);
    setEditing(row.id);
  };

  const toggleScope = (code: string) => {
    setDraft((current) => ({
      ...current,
      appliesTo: current.appliesTo.includes(code)
        ? current.appliesTo.filter((c) => c !== code)
        : [...current.appliesTo, code],
    }));
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const isNew = editing === "new";
      const response = await apiFetch(
        isNew ? `${SCORING_ROOT}/uis` : `${SCORING_ROOT}/uis/${editing}`,
        {
          method: isNew ? "POST" : "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            isNew
              ? {
                  sportCode: sport,
                  code: draft.code.trim(),
                  name: draft.name.trim(),
                  url: draft.url.trim(),
                  appliesTo: draft.appliesTo,
                  description: draft.description.trim() || null,
                }
              : {
                  name: draft.name.trim(),
                  url: draft.url.trim(),
                  appliesTo: draft.appliesTo,
                  description: draft.description.trim() || null,
                },
          ),
        },
      );
      if (!response.ok) {
        const body = await readJson(response);
        setFormError(String(body.detail ?? `the admin API answered ${response.status}`));
        return;
      }
      setEditing(null);
      await reload();
    } catch {
      setFormError("the admin API is not reachable");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: UiRow) => {
    if (!window.confirm(`Unregister ${row.name}? Fixtures that picked it fall back to the best match.`)) {
      return;
    }
    const response = await apiFetch(`${SCORING_ROOT}/uis/${row.id}`, { method: "DELETE" });
    if (response.ok) await reload();
  };

  const scopeLabel = (code: string) =>
    options.find((option) => option.code === code)?.label ?? code;
  const kinds = options.filter((option) => option.group === "kind");
  const configs = options.filter((option) => option.group === "config");
  const formOpen = editing !== null;
  const canSave =
    draft.name.trim() !== "" &&
    draft.url.trim() !== "" &&
    (editing !== "new" || draft.code.trim() !== "");

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Link className={styles.back} to={`/${sport}/scoring`}>
          <ArrowLeft size={14} />
          Scoring
        </Link>
        <h1 className={styles.title}>Scoring UIs</h1>
        <p className={styles.lede}>
          The screens that score {sport}{" "}fixtures — external pages, registered here by URL. Map one
          to the kinds or configs it is built for; the fixture form offers the matches, and the
          console opens the pick in its frame. The URL&rsquo;s origin is also the trust boundary:
          only messages from it are heard.
        </p>
      </header>

      <div className={styles.actions}>
        <button className={styles.primary} type="button" onClick={openNew}>
          <Plus size={15} />
          Register a scoring UI
        </button>
      </div>

      {problem && <p className={styles.note}>Nothing to show — {problem}.</p>}

      {formOpen && (
        <section className={styles.form} aria-label="Scoring UI details">
          <h2 className={styles.formTitle}>
            {editing === "new" ? "Register a scoring UI" : `Edit ${draft.name || draft.code}`}
          </h2>
          <div className={styles.grid2}>
            <label className={styles.field}>
              <span>Name</span>
              <input
                value={draft.name}
                placeholder="Strike Score"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Code</span>
              <input
                value={draft.code}
                disabled={editing !== "new"}
                placeholder="strike_score"
                spellCheck={false}
                title={
                  editing !== "new"
                    ? "The code is the registration's identity — set once"
                    : undefined
                }
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>URL</span>
            <input
              value={draft.url}
              placeholder="https://scoring.example.com"
              spellCheck={false}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            />
          </label>
          <div className={styles.field}>
            <span>Built for</span>
            <p className={styles.hint}>
              Nothing ticked means the whole sport. A config match beats a kind match when a
              fixture picks automatically.
            </p>
            {kinds.length > 0 && (
              <div className={styles.scopeRow}>
                <span className={styles.scopeGroup}>kinds</span>
                {kinds.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={styles.scope}
                    data-on={draft.appliesTo.includes(option.code) || undefined}
                    onClick={() => toggleScope(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            {configs.length > 0 && (
              <div className={styles.scopeRow}>
                <span className={styles.scopeGroup}>configs</span>
                {configs.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={styles.scope}
                    data-on={draft.appliesTo.includes(option.code) || undefined}
                    onClick={() => toggleScope(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className={styles.field}>
            <span>Description</span>
            <input
              value={draft.description}
              placeholder="What this screen is for (optional)"
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </label>
          {formError && <p className={styles.formError}>{formError}</p>}
          <div className={styles.formActions}>
            <button
              className={styles.primary}
              type="button"
              disabled={!canSave || saving}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : editing === "new" ? "Register" : "Save changes"}
            </button>
            <button className={styles.quiet} type="button" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <ul className={styles.list}>
        {rows.map((row) => (
          <li key={row.id} className={styles.row}>
            <span className={styles.rowIcon}>
              <Globe size={16} />
            </span>
            <span className={styles.rowBody}>
              <span className={styles.rowHead}>
                <strong>{row.name}</strong>
                <code className={styles.code}>{row.code}</code>
              </span>
              <a className={styles.url} href={row.url} target="_blank" rel="noreferrer">
                {row.url}
              </a>
              {row.description && <span className={styles.desc}>{row.description}</span>}
              <span className={styles.scopes}>
                {row.appliesTo.length === 0 ? (
                  <span className={styles.scopeChip} data-all="true">
                    whole sport
                  </span>
                ) : (
                  row.appliesTo.map((code) => (
                    <span key={code} className={styles.scopeChip}>
                      {scopeLabel(code)}
                    </span>
                  ))
                )}
              </span>
            </span>
            <span className={styles.rowActions}>
              <button type="button" title="Edit" onClick={() => openEdit(row)}>
                <Pencil size={14} />
              </button>
              <button
                type="button"
                title="Unregister — fixtures that picked it fall back to the best match"
                onClick={() => void remove(row)}
              >
                <Trash2 size={14} />
              </button>
            </span>
          </li>
        ))}
      </ul>

      {rows.length === 0 && !problem && !formOpen && (
        <p className={styles.note}>
          No scoring UIs registered for {sport} yet. Register one above — the fixture form and the
          console pick from this list.
        </p>
      )}
    </div>
  );
}
